import React, { useCallback, useRef } from 'react';
import {
  Animated,
  Pressable,
  PressableProps,
  StyleProp,
  ViewStyle,
  GestureResponderEvent,
} from 'react-native';
import { useReducedMotion } from '@/hooks/shared/useReducedMotion';

// Matches ModalButton's press spring so all press feedback moves the same way.
// Core Animated (not reanimated) to mirror AnimatedEntrance — the unit jest
// project has no reanimated mock.
const SPRING_CONFIG = { damping: 15, stiffness: 400, mass: 1 };

export interface PressableScaleProps extends PressableProps {
  /** Scale while pressed. 0.97 suits buttons; use ~0.98 for large cards. */
  scaleTo?: number;
  /**
   * Styles for the animated wrapper — the flex item when this sits in a grid
   * or row. Visual styles (and Pressable function styles) belong on `style`,
   * which is forwarded to the inner Pressable untouched.
   */
  containerStyle?: StyleProp<ViewStyle>;
  children: React.ReactNode;
}

/**
 * Pressable with a spring scale-down while pressed — the native counterpart
 * of the desktop cards' hover lift. Under reduced motion the scale is
 * suppressed; non-motion pressed styles from the caller still apply.
 */
export const PressableScale: React.FC<PressableScaleProps> = ({
  scaleTo = 0.97,
  containerStyle,
  onPressIn,
  onPressOut,
  disabled,
  children,
  ...pressableProps
}) => {
  const reduceMotion = useReducedMotion();
  const scale = useRef(new Animated.Value(1)).current;

  const springTo = useCallback(
    (toValue: number) => {
      Animated.spring(scale, { toValue, useNativeDriver: true, ...SPRING_CONFIG }).start();
    },
    [scale],
  );

  const handlePressIn = useCallback(
    (e: GestureResponderEvent) => {
      if (!disabled && !reduceMotion) springTo(scaleTo);
      onPressIn?.(e);
    },
    [disabled, reduceMotion, springTo, scaleTo, onPressIn],
  );

  const handlePressOut = useCallback(
    (e: GestureResponderEvent) => {
      if (!reduceMotion) springTo(1);
      onPressOut?.(e);
    },
    [reduceMotion, springTo, onPressOut],
  );

  return (
    <Animated.View style={[{ transform: [{ scale }] }, containerStyle]}>
      <Pressable
        disabled={disabled}
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        {...pressableProps}
      >
        {children}
      </Pressable>
    </Animated.View>
  );
};

PressableScale.displayName = 'PressableScale';
